import { useCallback, useSyncExternalStore } from "react";

/**
 * BİR MEDYA SORGUSU ŞU AN TUTUYOR MU — uygulamanın ORTAK kancası (9 Ekim 2026, kabuk turu).
 *
 * Aynı iş üç yerde üç kez yazılmıştı: `features/events/hooks/useMediaQuery.ts`, sohbetin
 * `useNarrowScreen`'i ve kabuğun kendi `matchMedia` etkisi. Kabuk da sohbetin kancasını
 * içe aktarıyordu — uygulama katmanı bir özellik klasörüne bağlıydı, yalnız bir eşik okumak
 * için. Ortak olan burada; `responsive-and-mobile.md`'nin «JS kırılımları tek kancadan okur»
 * kuralının dosyası bu (`useBreakpoint.ts` diye anılıyordu, hiç yazılmamıştı).
 *
 * RENDER SIRASINDA OKUNUYOR, bir etkide değil (`useSyncExternalStore`): ilk boyama zaten doğru
 * düzeni çiziyor, bir kare sonra zıplamıyor.
 *
 * `matchMedia` YOKSA `false` — sunucuda, eski bir tarayıcıda ya da kurulmamış bir test
 * ortamında geniş masaüstü düzeni varsayılan. Sohbet ve etkinlik kancaları bunu sormuyor ve
 * `matchMedia`'sız ortamda ilk okumada düşüyordu; burada sorulmasının sebebi o.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!mediaQueriesSupported()) return () => undefined;
      const media = window.matchMedia(query);
      media.addEventListener("change", onStoreChange);
      return () => media.removeEventListener("change", onStoreChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => (mediaQueriesSupported() ? window.matchMedia(query).matches : false),
    () => false,
  );
}

function mediaQueriesSupported(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

/**
 * Kabuğun iki eşiği, `responsive-and-mobile.md`'nin kırılım tablosundan. Sayı değil sorgu
 * metni: piksel eşiği JS'te yazılmıyor (aynı belge, HARD RULE), `app.css`'in kurallarıyla aynı
 * `rem` değerleri.
 */
export const MEDIA = {
  /** 768 pikselin altı (`md` öncesi): düzen şekil değiştiriyor — kitin telefon kuralı. */
  phone: "(max-width: 47.99rem)",
  /** 1024 ve üstü (`lg`): ray sabit sütun; altında çekmece. */
  wideShell: "(min-width: 64rem)",
  /**
   * 1024'ün altı — `wideShell`'in tersi, ama OLUMLU sorulmuş (31 Ekim 2026). Ayrı bir sorgu,
   * çünkü sorguyu karşılamayan ortam (sunucu çizimi, `matchMedia`'sız sınama) «geniş» sayılmalı:
   * `!wideShell` orada «dar» derdi ve her liste kartla açılırdı.
   */
  belowWideShell: "(max-width: 63.99rem)",
  /**
   * 1280 ve üstü (`xl`, UI/UX denetimi İ-B1): geniş tablonun bütün sütunlarının sığdığı eşik.
   * Altında sayfa ikincil sütunları `columnVisibility` ile gizler; birincil sütun sabitlenir.
   */
  desktop: "(min-width: 80rem)",
  /**
   * 1280'in altı — `desktop`'ın tersi, `belowWideShell` gibi OLUMLU sorulmuş (31 Ekim 2026): sorguyu
   * karşılamayan ortam «geniş» sayılmalı; `!desktop` orada her tablonun ikincil sütunlarını gizlerdi.
   */
  belowDesktop: "(max-width: 79.99rem)",
} as const;
